import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import axios from '../../api/axios';

const NvidiaAnalysis = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/NvidiaAnalysis/completeAnalysis');
      setAnalysisData(response.data.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch NVIDIA analysis data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
      </Container>
    );
  }

  if (!analysisData) {
    return (
      <Container>
        <Alert severity="info" sx={{ mt: 3 }}>No data available</Alert>
      </Container>
    );
  }

  const timelineData = analysisData.acquisitions.map(acq => {
    const date = new Date(acq.date);
    return {
      date: date.getTime(),
      dateLabel: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
      year: date.getFullYear(),
      company: acq.company,
      value: acq.valueMillions,
      techScore: acq.techImpactScore,
      clusterId: acq.clusterId,
      cluster: acq.clusterId === 1 ? 'AI Cluster' : 'Strategic Outlier',
      category: acq.category
    };
  }).sort((a, b) => a.date - b.date);

  const forecastData = analysisData.forecast?.forecastData?.map(item => ({
    year: item.year,
    actual: item.actualRevenue,
    predicted: item.predictedRevenue,
    lower: item.predictedRevenue - item.confidenceInterval,
    upper: item.predictedRevenue + item.confidenceInterval
  })) || [];

  const competitiveData = analysisData.competitiveAnalysis.competitors.map(comp => ({
    company: comp.company,
    innovation: comp.innovationScore,
    marketShare: comp.marketShare,
    maCount: comp.maCount
  }));

  const innovationRadarData = Object.entries(analysisData.innovationFactor.components).map(([key, value]) => ({
    metric: key,
    nvidia: value * 100,
    industry: 65
  }));

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        NVIDIA AI Chip Innovation Analysis
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="M&A Timeline" />
          <Tab label="Innovation Factor" />
          <Tab label="Revenue Forecast" />
          <Tab label="Key Findings" />
        </Tabs>
      </Box>

      {}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                M&A Timeline with DBSCAN Clustering (eps=180, minPts=4)
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    name="Year"
                    type="number"
                    domain={[1999, 2026]}
                    ticks={[2000, 2003, 2007, 2011, 2015, 2019, 2021, 2022, 2023, 2024, 2025]}
                    label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
                    angle={0}
                    interval={0}
                  />
                  <YAxis
                    dataKey="value"
                    name="Value (M$)"
                    scale="log"
                    domain={[10, 10000]}
                    label={{ value: 'Acquisition Value (M$)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => `$${value}M`}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <Box sx={{ bgcolor: 'white', p: 2, border: '1px solid #ccc', borderRadius: 1, boxShadow: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{data.company}</Typography>
                            <Typography variant="body2">Date: {data.dateLabel}</Typography>
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              Value: ${data.value.toLocaleString()}M
                            </Typography>
                            <Typography variant="body2">Category: {data.category}</Typography>
                            <Typography variant="body2">Tech Impact: {data.techScore}/10</Typography>
                            <Box component="span" sx={{
                              display: 'inline-block',
                              mt: 1,
                              px: 1,
                              py: 0.5,
                              bgcolor: data.clusterId === 1 ? '#44ff44' : '#ff4444',
                              color: 'black',
                              borderRadius: 1,
                              fontSize: '0.75rem'
                            }}>
                              {data.clusterId === 1 ? 'AI Dense Cluster' : 'Strategic Outlier'}
                            </Box>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Scatter
                    name="Cluster 1: AI Infrastructure (2019-2022)"
                    data={timelineData.filter(d => d.clusterId === 1)}
                    fill="#44ff44"
                    shape="circle"
                  />
                  <Scatter
                    name="Cluster 2: AI Software (2024-2025)"
                    data={timelineData.filter(d => d.clusterId === 2)}
                    fill="#4488ff"
                    shape="star"
                  />
                  <Scatter
                    name="Strategic Outliers"
                    data={timelineData.filter(d => d.clusterId === -1)}
                    fill="#ff4444"
                    shape="diamond"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Technology Impact Score Evolution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    type="number"
                    domain={[1999, 2026]}
                    ticks={[2000, 2003, 2007, 2011, 2015, 2019, 2021, 2022, 2023, 2024, 2025]}
                    label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
                    interval={0}
                  />
                  <YAxis
                    domain={[0, 10]}
                    label={{ value: 'Technology Impact Score', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    labelFormatter={(value) => `Year: ${value}`}
                    formatter={(value) => [`${value}/10`, 'Tech Impact Score']}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="techScore"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                    name="Tech Impact Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Innovation Factor Components
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={innovationRadarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="NVIDIA"
                    dataKey="nvidia"
                    stroke="#44ff44"
                    fill="#44ff44"
                    fillOpacity={0.6}
                  />
                  <Radar
                    name="Industry Avg"
                    dataKey="industry"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h4" color="primary" align="center">
                  Innovation Factor: {analysisData.innovationFactor.overallScore}/10
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary">
                  {analysisData.innovationFactor.interpretation}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Competitive Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={competitiveData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="company" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="innovation" fill="#8884d8" name="Innovation Score" />
                  <Bar dataKey="marketShare" fill="#82ca9d" name="Market Share %" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {}
      {currentTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Revenue Forecast with M&A Probability
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                <strong>Algorithm:</strong> {analysisData.forecast?.method || 'SSA (Singular Spectrum Analysis)'}
                <br />
                <strong>Model Accuracy:</strong> {analysisData.forecast?.modelAccuracy ? (analysisData.forecast.modelAccuracy * 100).toFixed(0) + '%' : 'N/A'}
                <br />
                <strong>Method:</strong> Time series decomposition into trend, seasonality, and noise components with 95% confidence intervals
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={forecastData} margin={{ top: 20, right: 30, bottom: 20, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis
                    label={{ value: 'Revenue (Million $)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}B`}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (value === null || value === undefined) return ['N/A', name];
                      return [`$${value.toLocaleString()}M`, name];
                    }}
                    labelFormatter={(label) => `Year: ${label}`}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Area
                    type="monotone"
                    dataKey="upper"
                    stroke="none"
                    fill="#82ca9d"
                    fillOpacity={0.2}
                    name="Confidence Upper Bound"
                  />
                  <Area
                    type="monotone"
                    dataKey="lower"
                    stroke="none"
                    fill="#82ca9d"
                    fillOpacity={0.2}
                    name="Confidence Lower Bound"
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#8884d8"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#8884d8' }}
                    name="Actual Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="predicted"
                    stroke="#ff7300"
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ r: 5, fill: '#ff7300' }}
                    name="Predicted Revenue (SSA)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Forecasting Method
                    </Typography>
                    <Typography variant="h6">
                      {analysisData.forecast.method}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Model Accuracy: {(analysisData.forecast.modelAccuracy * 100).toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      2030 Revenue Forecast
                    </Typography>
                    <Typography variant="h4">
                      ${(forecastData[forecastData.length - 1]?.predicted / 1000).toFixed(1)}B
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ±${((forecastData[forecastData.length - 1]?.upper - forecastData[forecastData.length - 1]?.predicted) / 1000).toFixed(1)}B
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Main Findings
              </Typography>
              {analysisData.keyFindings.mainFindings.map((finding, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    • {finding}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Strategic Implications
              </Typography>
              {analysisData.keyFindings.strategicImplications.map((implication, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    • {implication}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Clustering Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Silhouette Score
                  </Typography>
                  <Typography variant="h5">
                    {analysisData.clusteringResult.silhouetteScore.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Dense Cluster Points
                  </Typography>
                  <Typography variant="h5">
                    {analysisData.clusteringResult.clusteredPoints}/{analysisData.clusteringResult.totalPoints}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Noise Ratio
                  </Typography>
                  <Typography variant="h5">
                    {((analysisData.clusteringResult.noisePoints / analysisData.clusteringResult.totalPoints) * 100).toFixed(1)}%
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Parameters
                  </Typography>
                  <Typography variant="h5">
                    eps=180, minPts=4
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default NvidiaAnalysis;